import React, { useContext } from "react";

import { Link, useLocation, useParams } from "react-router-dom";

import { WrappedLink } from "@components/navigation";
import { AdditionalOfferInteractionTypeEnum } from "@data-types/analytics-tracker/AdditionalOfferInteractionTypeEnum";
import { TLivePageUrlParams } from "@data-types/offer/live/LivePageUrlParams";
import { NotificationFavoritesButton } from "@features/notifications/notification-favorites/mobile";
import { EventContext, LookupContext, SportOfferContext } from "@gp/components";
import { history } from "@state";
import { useOfferContext } from "@state/hooks/offer/useOfferContext";
import { useOfferOptionsContext } from "@state/hooks/offer/useOfferOptionsContext";
import { THEME_SETTINGS } from "@theme-settings";
import { analyticsTracker } from "@utils/analytics";
import { buildRoute } from "@utils/common/buildOfferRoute";
import classNames from "classnames";
import { observer } from "mobx-react";

import BetBuilderButton from "../../button/BetBuilderButton";
import StatisticsButton from "../../button/StatisticsButton";
import { LiveStreamIcon } from "../../icons/LiveStreamIcon";
import { OutrightEventDetails } from "./OutrightEventDetails";
import { AdditionalOfferCount } from "./components/AdditionalOfferCount";
import { Competitors } from "./components/Competitors";
import { EventFavoritesIcon } from "./components/EventFavoritesIcon";
import { MatchIconAndName } from "./components/MatchIconAndName";
import { EventResult } from "./components/event-result/EventResult";
import { MatchTimeInfo } from "./components/match-time-info/MatchTimeInfo";

export const EventDetails = observer(function EventDetails(props: {
	isExpanded?: boolean;
	toggleSpecial?: (eventId: string) => void;
	mainOfferCount?: number;
	displaySportIcon?: boolean;
	shouldHideCollapseIcon?: boolean;
}) {
	const event = useContext(EventContext);

	const offerStore = useOfferContext();
	const offerOptions = useOfferOptionsContext();

	const { culture, period } = useParams<{
		culture: string;
		period: string;
	}>();
	const params = useParams<TLivePageUrlParams>();
	const eventOffer = offerStore.eventKeysMap.get(event.id);

	const sport = useContext(SportOfferContext);
	const lookupContext = useContext(LookupContext);

	const homeTeam = lookupContext.teams.get(event.teamOneId || "");
	const awayTeam = lookupContext.teams.get(event.teamTwoId || "");

	const isLivePage = useLocation().pathname.includes("live/");
	let url = buildRoute.eventCanonical({
		culture,
		period,
		eventIds: event.id,
	});
	if (offerOptions.eventNavigation) {
		url = offerOptions.eventNavigation(event.id);
	} else if (isLivePage) {
		url = `/${culture}/live/${params.section}/event/${event.id}`;
	} else if (event.isLive) {
		if (location.pathname.includes("home")) {
			if (event.isTopEvent) {
				url = `/${culture}/live/highlights/event/${event.id}`;
			} else {
				url = `/${culture}/live/matches/event/${event.id}`;
			}
		} else {
			url = `/${culture}/live/matches/event/${event.id}`;
		}
	}

	const headerClasses = classNames("match--special__header", {
		"is-collapsed": !props.isExpanded,
		"is-expanded": props.isExpanded,
	});

	const toggleBtnClasses = classNames(
		"match--special__toggle btn btn--square btn--square--sml btn--ghost",
		{
			"is-collapsed": !props.isExpanded,
			"is-expanded": props.isExpanded,
			"is-empty": eventOffer?.size == null || eventOffer.size < 1,
		}
	);

	const toggleIconClasses = classNames("u-icon u-icon--tny", {
		"u-icon--caret--down": !props.isExpanded,
		"u-icon--caret--up": props.isExpanded,
	});

	if (event.isOutright) {
		return (
			<>
				<div className={headerClasses}>
					<Link
						to={{
							pathname: url,
							state: {
								prevUrl:
									history.location.pathname +
									history.location.search,
								eventIdsForEventSwitcher:
									offerStore.eventIdsForEventSwitcher,
								headerInfo: offerStore.getHeaderInfo,
							},
						}}
						onClick={() => {
							analyticsTracker.trackAdditionalOfferInteraction(
								event.isLive
									? AdditionalOfferInteractionTypeEnum.LiveAdditionalOffer
									: AdditionalOfferInteractionTypeEnum.PrematchAdditionalOffer,
								sport.abrv,
								homeTeam?.abrv,
								awayTeam?.abrv
							);
						}}
						className={classNames("match--special__anchor")}
					>
						<OutrightEventDetails />
					</Link>

					{!props.shouldHideCollapseIcon && (
						<button
							type="button"
							data-type="sport"
							className={toggleBtnClasses}
							onClick={() => {
								props.toggleSpecial?.(event.id);
							}}
						>
							<i className={toggleIconClasses} />
						</button>
					)}
				</div>
			</>
		);
	}

	return (
		<WrappedLink
			to={{
				pathname: url,
				state: {
					prevUrl:
						history.location.pathname + history.location.search,
					eventIdsForEventSwitcher:
						offerStore.eventIdsForEventSwitcher,
					headerInfo: offerStore.getHeaderInfo,
				},
			}}
			onClick={() => {
				analyticsTracker.trackAdditionalOfferInteraction(
					event.isLive
						? AdditionalOfferInteractionTypeEnum.LiveAdditionalOffer
						: AdditionalOfferInteractionTypeEnum.PrematchAdditionalOffer,
					sport.abrv,
					homeTeam?.abrv,
					awayTeam?.abrv
				);
			}}
			className={classNames("match__anchor")}
		>
			<div className="match__table">
				<>
					<>
						<div className="match__details">
							<div className="match__details__wrapper">
								<div className="match__details__info">
									<MatchTimeInfo />
									{!offerStore.isGroupedByTournament && (
										<MatchIconAndName />
									)}
								</div>

								<div className="match__details__actions">
									{THEME_SETTINGS.features
										.isFavoritesEnabled && (
										<NotificationFavoritesButton
											eventId={event.id}
										/>
									)}
									{WEBPACK_IS_BET_BUILDER_ENABLED && (
										<BetBuilderButton
											isOfferButton
											isSmallIcon
										/>
									)}
									<StatisticsButton
										isOfferButton
										isSmallIcon
									/>
									<LiveStreamIcon
										liveStreamStatus={
											event?.liveStreamStatus
										}
									/>
									<EventFavoritesIcon />
								</div>
							</div>
						</div>
					</>
					<div className="match__event">
						<Competitors />
						<EventResult />
						<AdditionalOfferCount
							count={
								event.totalOfferCount -
								(props.mainOfferCount || 0)
							}
						/>
					</div>
				</>
			</div>
		</WrappedLink>
	);
});
